/**
 * Cloud-browser availability state, per thread.
 *
 * Reads the T3 server's `/api/amoreg/preview?threadId=…` endpoint (see
 * server `amoreg/browserbase/browserPreviewHttp`) and decodes it with Schema.
 * The server owns the truth: `available` says whether this server runs a
 * browser host at all, `live` whether the thread's browser is running (created
 * lazily on first operation), and `liveViewUrl` the provider's interactive
 * Live View when it has one (Browserbase; the London nodes have none).
 *
 * The preview surface renders the browser panel only when `available` is true,
 * and a browser only once one is live — the Live View iframe when there is a
 * URL, otherwise our own screencast frames — so nothing appears for a
 * capability the server can't back.
 *
 * @module state/browserPreview
 */
import { useAtomValue } from "@effect/atom-react";
import * as Effect from "effect/Effect";
import * as Layer from "effect/Layer";
import * as Option from "effect/Option";
import * as Schema from "effect/Schema";
import { AsyncResult, Atom } from "effect/unstable/reactivity";
import { useCallback, useEffect, useMemo, useState } from "react";

import { resolvePrimaryEnvironmentHttpUrl } from "../environments/primary/target";
import { appAtomRegistry } from "../rpc/atomRegistry";

const SnapshotSchema = Schema.Struct({
  available: Schema.Boolean,
  liveViewUrl: Schema.NullOr(Schema.String),
  // Absent on older servers; treated as false.
  dormant: Schema.optional(Schema.Boolean),
  // True once the thread's agent /mcp preview credential is minted; absent on
  // older servers.
  agentReady: Schema.optional(Schema.Boolean),
  // True while the thread's live browser carries the owner's saved logins
  // (AMO-152); absent on older servers.
  savedLogins: Schema.optional(Schema.Boolean),
  // Opaque key of the running browser session, any provider (AMO-179); absent
  // on older servers, which only ever had a live view.
  live: Schema.optional(Schema.NullOr(Schema.String)),
  // Place in line for a free browser while every browser is in use (AMO-179).
  waiting: Schema.optional(Schema.NullOr(Schema.Number)),
  // The agent's preview tab (AMO-232); absent on older servers.
  agentTab: Schema.optional(Schema.NullOr(Schema.String)),
});
type Snapshot = typeof SnapshotSchema.Type;

/**
 * The running session's key, on any provider. Older servers report no `live`:
 * their live view was the only sign of a browser.
 */
export const liveSessionOf = (
  snapshot: Pick<Snapshot, "live" | "liveViewUrl"> | null,
): string | null => {
  if (snapshot === null) return null;
  return snapshot.live !== undefined ? snapshot.live : snapshot.liveViewUrl;
};

/** No cloud browser configured on this server (endpoint 404 / unset key). */
const UNAVAILABLE: Snapshot = { available: false, liveViewUrl: null };

const decodeSnapshot = Schema.decodeUnknownEffect(SnapshotSchema);

class BrowserPreviewRequestError extends Schema.TaggedErrorClass<BrowserPreviewRequestError>()(
  "BrowserPreviewRequestError",
  { status: Schema.Number },
) {}

const fetchSnapshot = (threadId: string) =>
  Effect.gen(function* () {
    if (threadId.length === 0) return UNAVAILABLE;
    const url = resolvePrimaryEnvironmentHttpUrl("/api/amoreg/preview", { threadId });
    const response = yield* Effect.tryPromise(() => fetch(url, { credentials: "include" }));
    // 404 = this server has no cloud-browser feature; a normal state.
    if (response.status === 404) return UNAVAILABLE;
    if (!response.ok) {
      return yield* Effect.fail(new BrowserPreviewRequestError({ status: response.status }));
    }
    const json = yield* Effect.tryPromise(() => response.json());
    return yield* decodeSnapshot(json);
  });

const browserPreviewRuntime = Atom.runtime(Layer.empty);
const browserPreviewAtom = Atom.family((threadId: string) =>
  browserPreviewRuntime
    .atom(fetchSnapshot(threadId))
    .pipe(Atom.withLabel(`web-amoreg:browser-preview:${threadId}`)),
);

export interface BrowserPreviewView {
  /** False when this server runs no Browserbase host. */
  readonly available: boolean;
  /**
   * Interactive Live View URL, when the provider has one (Browserbase). The
   * London nodes have none; the surface then draws our own screencast.
   */
  readonly liveViewUrl: string | null;
  /**
   * Opaque key of the thread's running browser session, new for every
   * session; null when no browser runs. The surface's "is there a browser".
   */
  readonly liveSession: string | null;
  /** The thread's place in line while every browser is in use, else null. */
  readonly waiting: number | null;
  /**
   * The agent's preview tab (AMO-232), null when unknown. The provider's live
   * view shows that tab only; the person's own tabs draw their own frames.
   */
  readonly agentTab: string | null;
  /**
   * True while the server released the thread's idle session but kept its
   * state (storage context + last frame). The surface shows the frozen frame
   * with a resume affordance and polls nothing fast.
   */
  readonly dormant: boolean;
  /**
   * True once the agent's way into the shared browser (the per-thread /mcp
   * credential) exists. The Browser surface stays disabled while false, so a
   * human never opens a browser the agent cannot join.
   */
  readonly agentReady: boolean;
  /** True while this browser carries the owner's saved logins (first-use notice). */
  readonly savedLogins: boolean;
  /** True until the first response arrives. */
  readonly isPending: boolean;
  /** Re-fetch — used to poll for a live session after the agent opens a browser. */
  readonly refresh: () => void;
}

export type BrowserChromeAction =
  | "navigate"
  | "back"
  | "forward"
  | "refresh"
  | "status"
  | "open"
  | "click"
  | "scroll"
  | "press"
  | "type"
  | "resize"
  | "setColorScheme"
  | "hardReload"
  // Reports human engagement with the live-view iframe (whose input never
  // reaches the broker) so the server's idle sweep does not reap it.
  | "touch";

export interface BrowserChromeStatus {
  readonly url: string | null;
  readonly title: string | null;
  /** True while the page's main frame is between commit and load. */
  readonly loading: boolean;
  /** False when the chat has no running browser any more (it went to sleep, or ended). */
  readonly available: boolean;
}

/**
 * Drive the thread's cloud browser. Dispatches through the server's
 * `/api/amoreg/preview/op` route, which routes into the same
 * PreviewAutomationBroker the agent uses — human and agent share one browser.
 * Chrome-row actions pass `url`; the screencast surface passes pointer and
 * keyboard params (x/y, deltaX/deltaY, key, text). Returns the operation
 * result (a status object for navigate/status/open).
 */
/** A failed browser action, with the server's error code when it gave one. */
export class BrowserActionError extends Error {
  constructor(
    message: string,
    readonly code: string | null,
  ) {
    super(message);
    this.name = "BrowserActionError";
  }
}

/**
 * One `/api/amoreg/preview/op` request; its `result` as the server sent it.
 * Throws BrowserActionError on a refusal.
 */
async function postBrowserOp(
  threadId: string,
  action: string,
  params: Record<string, string>,
): Promise<unknown> {
  const target = resolvePrimaryEnvironmentHttpUrl("/api/amoreg/preview/op", {
    threadId,
    action,
    ...params,
  });
  const response = await fetch(target, { method: "POST", credentials: "include" });
  if (!response.ok) {
    // Failed ops answer `{ok:false, error:{code, message}}` with the upstream
    // detail (e.g. the Browserbase quota message) — surface it verbatim.
    let detail = "";
    let code: string | null = null;
    try {
      const body = (await response.json()) as {
        readonly error?: { readonly message?: unknown; readonly code?: unknown };
      };
      if (typeof body.error?.message === "string") detail = body.error.message;
      if (typeof body.error?.code === "string") code = body.error.code;
    } catch {
      // Plain-text failure body; fall through to the generic message.
    }
    throw new BrowserActionError(detail || `Browser action ${action} failed (${response.status}).`, code);
  }
  const payload = (await response.json()) as { readonly result?: unknown };
  return payload.result ?? null;
}

export async function sendBrowserChromeAction(
  threadId: string,
  action: BrowserChromeAction,
  params: Record<string, string> = {},
): Promise<BrowserChromeStatus | null> {
  const result = (await postBrowserOp(threadId, action, params)) as {
    readonly url?: string | null;
    readonly title?: string | null;
    readonly loading?: boolean;
    readonly available?: boolean;
  } | null;
  if (!result || typeof result !== "object") return null;
  return {
    url: result.url ?? null,
    title: result.title ?? null,
    loading: result.loading === true,
    available: result.available !== false,
  };
}

/**
 * The element under a point of the page (page CSS px), for the element picker
 * (AMO-234); null where there is none. `session`: the browser session the
 * point was taken on (the server refuses a point taken on another).
 */
export async function inspectBrowserElement(
  threadId: string,
  point: { readonly x: number; readonly y: number },
  session: string | null,
  tab: string | null = null,
): Promise<unknown> {
  return postBrowserOp(threadId, "inspect", {
    x: String(point.x),
    y: String(point.y),
    ...(session ? { session } : {}),
    ...(tab ? { tab } : {}),
  });
}

/** One of the browser's tabs, as the server lists them for the tab strip. */
export interface BrowserTabInfo {
  readonly id: string;
  readonly url: string;
  readonly title: string;
  readonly loading: boolean;
  /** The tab that opened it (a popup's). */
  readonly openerId: string | null;
}

export interface BrowserTabsView {
  /** The tab the agent works in. */
  readonly agentTab: string;
  readonly tabs: ReadonlyArray<BrowserTabInfo>;
}

/** The browser's tabs (a sleeping browser lists the ones it had); null when it has none. */
export async function fetchBrowserTabs(threadId: string): Promise<BrowserTabsView | null> {
  const result = (await postBrowserOp(threadId, "tabs", {})) as Partial<BrowserTabsView> | null;
  if (!result || typeof result !== "object" || typeof result.agentTab !== "string" || !Array.isArray(result.tabs)) {
    return null;
  }
  return { agentTab: result.agentTab, tabs: result.tabs };
}

/** Open a tab (on `url`, else the start page); its id. */
export async function openBrowserTab(threadId: string, url: string | null = null): Promise<string | null> {
  const result = (await postBrowserOp(threadId, "newTab", url ? { url } : {})) as { readonly tabId?: unknown } | null;
  return typeof result?.tabId === "string" ? result.tabId : null;
}

/** Close one of the browser's tabs. */
export async function closeBrowserTab(threadId: string, tab: string): Promise<void> {
  await postBrowserOp(threadId, "closeTab", { tab });
}

/** One console line of the page (the server's PreviewAutomationConsoleEntry). */
export interface BrowserConsoleLine {
  readonly level: string;
  readonly text: string;
  readonly timestamp: string;
}

/** One network request of the page (the server's PreviewAutomationNetworkEntry). */
export interface BrowserNetworkRequest {
  readonly url: string;
  readonly method: string;
  readonly status: number | null;
  readonly failed: boolean;
  readonly errorText?: string;
  readonly timestamp: string;
}

export interface BrowserPageLogs {
  readonly console: ReadonlyArray<BrowserConsoleLine>;
  readonly network: ReadonlyArray<BrowserNetworkRequest>;
}

/** The page's latest console lines and network requests (AMO-234); null when no browser runs. */
export async function fetchBrowserPageLogs(threadId: string): Promise<BrowserPageLogs | null> {
  const result = (await postBrowserOp(threadId, "logs", {})) as Partial<BrowserPageLogs> | null;
  if (!result || typeof result !== "object") return null;
  return {
    console: Array.isArray(result.console) ? result.console : [],
    network: Array.isArray(result.network) ? result.network : [],
  };
}

/**
 * URL of the latest screencast frame (JPEG; 204 until a browser exists). The
 * `nonce` busts any intermediary caching per poll.
 */
export function browserFrameUrl(threadId: string, nonce: number, tab: string | null = null): string {
  return resolvePrimaryEnvironmentHttpUrl("/api/amoreg/preview/frame", {
    threadId,
    // A panel tab's own frames (AMO-232); the agent's tab's without one.
    ...(tab ? { tab } : {}),
    nonce: String(nonce),
  });
}

/** The same address on the WebSocket scheme. */
export function toWebSocketUrl(httpUrl: string): string {
  const url = new URL(httpUrl);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  return url.toString();
}

/** One pushed frame: its page size, then the JPEG (server `encodeFrameMessage`). */
export function decodeFrameMessage(
  data: ArrayBuffer,
): { readonly width: number; readonly height: number; readonly jpeg: Uint8Array<ArrayBuffer> } | null {
  if (data.byteLength <= 8) return null;
  const header = new DataView(data);
  return { width: header.getUint32(0), height: header.getUint32(4), jpeg: new Uint8Array(data, 8) };
}

/** What the server says back about a person's input (its `humanInputPlayer`). */
export type BrowserInputReply =
  | { readonly t: "cursor"; readonly value: string }
  /**
   * The answer to copy `id` (null from a server that sends none).
   * `truncated`: the page's selection was longer than what was copied.
   */
  | { readonly t: "clipboard"; readonly id: number | null; readonly text: string; readonly truncated: boolean };

/** A person's mouse or keyboard input for the page (the server's `lib/humanInput`). */
export type BrowserInputMessage = (
  | { readonly t: "move"; readonly x: number; readonly y: number }
  | {
      readonly t: "down" | "up";
      readonly x: number;
      readonly y: number;
      readonly button: "left" | "middle" | "right";
      readonly clicks: number;
    }
  | { readonly t: "wheel"; readonly x: number; readonly y: number; readonly dx: number; readonly dy: number }
  | { readonly t: "keydown" | "keyup"; readonly key: string }
  | { readonly t: "text"; readonly text: string }
  /** `id` pairs the request with its answer, so a late answer completes nothing newer. */
  | { readonly t: "copy"; readonly id: number }
) & {
  /** The browser session it was made for (`live`); the server drops it for any other. */
  readonly s?: string;
};

export interface BrowserFrameHandlers {
  /** A newer frame; `size` is the page's CSS size when the server sent it. */
  readonly onFrame: (blob: Blob, size: { readonly width: number; readonly height: number } | null) => void;
  /** The thread's browser has gone (204 / "ended"): re-check availability. */
  readonly onEnded: () => void;
  /** The page's cursor, or text copied from it, after the person's input. */
  readonly onReply?: (reply: BrowserInputReply) => void;
}

/** Stops following; `send` carries input over the open socket (false when there is none). */
export type BrowserFramesFollower = (() => void) & {
  readonly send: (message: BrowserInputMessage) => boolean;
  /** Whether input can go over the socket right now. */
  readonly isOpen: () => boolean;
};

const parseInputReply = (text: string): BrowserInputReply | null => {
  try {
    const parsed = JSON.parse(text) as Partial<{ t: unknown; value: unknown; text: unknown; truncated: unknown; id: unknown }>;
    if (parsed.t === "cursor" && typeof parsed.value === "string") return { t: "cursor", value: parsed.value };
    if (parsed.t === "clipboard" && typeof parsed.text === "string") {
      return {
        t: "clipboard",
        id: typeof parsed.id === "number" ? parsed.id : null,
        text: parsed.text,
        truncated: parsed.truncated === true,
      };
    }
  } catch {
    // Not a reply.
  }
  return null;
};

/** A socket that closes this soon after opening, before any frame, counts as unusable. */
const FRAME_SOCKET_SETTLE_MS = 3_000;
/** A socket still not open after this long is abandoned (a proxy that swallows upgrades hangs it). */
const FRAME_SOCKET_OPEN_TIMEOUT_MS = 5_000;
const FRAME_SOCKET_RECONNECT_MS = 1_000;
const FRAME_SOCKET_MAX_RECONNECTS = 5;

/**
 * Follow the thread's screencast frames. They are pushed over one WebSocket
 * (`/api/amoreg/preview/frames`) as the page paints; when the socket cannot
 * open, or keeps dropping, this falls back to polling `/frame` — one request
 * in flight at most, never parallel (the per-origin connection budget, §4 of
 * docs/browser-automation.md). Returns the stop function, which also sends a
 * person's input over the socket while it is open ("take control"): polling
 * has no way back, so callers fall back to the op route then.
 */
export function followBrowserFrames(
  threadId: string,
  handlers: BrowserFrameHandlers,
  pollIntervalMs: number,
  /** The panel tab shown and driven (AMO-232); the agent's tab without one. */
  tab: string | null = null,
): BrowserFramesFollower {
  let stopped = false;
  let socket: WebSocket | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let reconnects = 0;

  const poll = () => {
    let nonce = 0;
    const tick = async () => {
      nonce += 1;
      try {
        const response = await fetch(browserFrameUrl(threadId, nonce, tab), { credentials: "include" });
        if (stopped) return;
        if (response.status === 204) {
          handlers.onEnded();
          return;
        }
        if (response.status !== 200) return;
        const width = Number(response.headers.get("x-frame-width"));
        const height = Number(response.headers.get("x-frame-height"));
        const size =
          Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0 ? { width, height } : null;
        const blob = await response.blob();
        if (!stopped) handlers.onFrame(blob, size);
      } catch {
        // Transient failure; the next tick retries.
      }
    };
    const loop = async () => {
      await tick();
      if (stopped) return;
      timer = globalThis.setTimeout(loop, pollIntervalMs);
    };
    void loop();
  };

  const connect = () => {
    let openedAt = 0;
    let sawFrame = false;
    let ws: WebSocket;
    try {
      ws = new WebSocket(
        toWebSocketUrl(
          resolvePrimaryEnvironmentHttpUrl("/api/amoreg/preview/frames", { threadId, ...(tab ? { tab } : {}) }),
        ),
      );
    } catch {
      poll();
      return;
    }
    socket = ws;
    ws.binaryType = "arraybuffer";
    let abandoned = false;
    const openTimer = globalThis.setTimeout(() => {
      if (openedAt !== 0 || stopped) return;
      // Neither open nor closed: poll instead, and ignore this socket's close.
      abandoned = true;
      socket = null;
      ws.close();
      poll();
    }, FRAME_SOCKET_OPEN_TIMEOUT_MS);
    ws.addEventListener("open", () => {
      openedAt = Date.now();
      globalThis.clearTimeout(openTimer);
    });
    ws.addEventListener("message", (event) => {
      if (stopped) return;
      if (typeof event.data === "string") {
        if (event.data === "ended") {
          handlers.onEnded();
          return;
        }
        const reply = parseInputReply(event.data);
        if (reply) handlers.onReply?.(reply);
        return;
      }
      const frame = decodeFrameMessage(event.data as ArrayBuffer);
      if (!frame) return;
      sawFrame = true;
      reconnects = 0;
      handlers.onFrame(new Blob([frame.jpeg], { type: "image/jpeg" }), { width: frame.width, height: frame.height });
    });
    ws.addEventListener("close", () => {
      globalThis.clearTimeout(openTimer);
      if (stopped || abandoned) return;
      socket = null;
      const unusable = openedAt === 0 || (!sawFrame && Date.now() - openedAt < FRAME_SOCKET_SETTLE_MS);
      if (unusable || reconnects >= FRAME_SOCKET_MAX_RECONNECTS) {
        poll();
        return;
      }
      reconnects += 1;
      timer = globalThis.setTimeout(connect, FRAME_SOCKET_RECONNECT_MS);
    });
  };

  connect();
  const stop = () => {
    stopped = true;
    if (timer !== null) globalThis.clearTimeout(timer);
    socket?.close();
  };
  const isOpen = (): boolean => !stopped && socket !== null && socket.readyState === WebSocket.OPEN;
  const send = (message: BrowserInputMessage): boolean => {
    const open = socket;
    if (stopped || !open || open.readyState !== WebSocket.OPEN) return false;
    open.send(JSON.stringify(message));
    return true;
  };
  return Object.assign(stop, { send, isOpen });
}

export function useBrowserPreview(threadId: string | null): BrowserPreviewView {
  const key = threadId ?? "";
  const atom = useMemo(() => browserPreviewAtom(key), [key]);
  const result = useAtomValue(atom);

  const refresh = useCallback(() => {
    appAtomRegistry.refresh(atom);
  }, [atom]);

  return useMemo(() => {
    const snapshot = Option.getOrNull(AsyncResult.value(result));
    return {
      available: snapshot?.available ?? false,
      liveViewUrl: snapshot?.liveViewUrl ?? null,
      liveSession: liveSessionOf(snapshot),
      waiting: snapshot?.waiting ?? null,
      agentTab: snapshot?.agentTab ?? null,
      dormant: snapshot?.dormant === true,
      agentReady: snapshot?.agentReady === true,
      savedLogins: snapshot?.savedLogins === true,
      isPending: result.waiting && snapshot === null,
      refresh,
    };
  }, [result, refresh]);
}

/**
 * While dormant the server's frame is frozen, so instead of the fast frame
 * loop the hook fetches it once and only revalidates availability at this
 * cadence — enough to notice the agent waking the browser, cheap enough that
 * an idle surface generates no meaningful load.
 */
const DORMANT_REVALIDATE_MS = 4_000;

/**
 * Latest screencast frame of the thread's cloud browser as an object URL.
 * One request in flight at most; a 204 (session ended) clears the frame and
 * re-checks availability. Used by the panel surface and the floating player.
 */
export function useBrowserFrame(
  threadId: string | null,
  active: boolean,
  intervalMs = 160,
  /** The panel tab shown (AMO-232); the agent's tab without one. */
  tab: string | null = null,
): { frameSrc: string | null } {
  const { refresh, dormant } = useBrowserPreview(threadId);
  const [frameSrc, setFrameSrc] = useState<string | null>(null);
  useEffect(() => {
    if (!active || !threadId) return;
    let cancelled = false;
    let previous: string | null = null;
    let timer: number | null = null;
    let nonce = 0;
    const tick = async () => {
      nonce += 1;
      try {
        const response = await fetch(browserFrameUrl(threadId, nonce, tab), {
          credentials: "include",
        });
        if (cancelled) return;
        if (response.status === 204) {
          if (previous) {
            URL.revokeObjectURL(previous);
            previous = null;
          }
          setFrameSrc(null);
          refresh();
          return;
        }
        if (response.status !== 200) return;
        const blob = await response.blob();
        if (cancelled) return;
        const objectUrl = URL.createObjectURL(blob);
        setFrameSrc(objectUrl);
        if (previous) URL.revokeObjectURL(previous);
        previous = objectUrl;
      } catch {
        // Next tick retries.
      }
    };
    const cleanup = () => {
      cancelled = true;
      if (timer !== null) window.clearTimeout(timer);
      if (previous) URL.revokeObjectURL(previous);
      // Drop the revoked URL from state so a thread switch or deactivation
      // never renders a dead blob reference.
      setFrameSrc(null);
    };
    if (dormant) {
      // Frozen face of a released session: one fetch, then a slow
      // availability revalidation instead of the frame loop. When the
      // browser wakes, `dormant` flips and the effect re-runs into the loop.
      void tick();
      timer = window.setInterval(refresh, DORMANT_REVALIDATE_MS);
      return cleanup;
    }
    // Live: frames pushed over the frames socket, or polled when it cannot open.
    const stopFollowing = followBrowserFrames(
      threadId,
      {
        onFrame: (blob) => {
          if (cancelled) return;
          const objectUrl = URL.createObjectURL(blob);
          setFrameSrc(objectUrl);
          if (previous) URL.revokeObjectURL(previous);
          previous = objectUrl;
        },
        onEnded: () => {
          if (previous) {
            URL.revokeObjectURL(previous);
            previous = null;
          }
          setFrameSrc(null);
          refresh();
        },
      },
      intervalMs,
      tab,
    );
    return () => {
      stopFollowing();
      cleanup();
    };
  }, [active, threadId, intervalMs, refresh, dormant, tab]);
  return { frameSrc };
}
